/**
 * CompactClosedExplainer — progressive-disclosure explainer for compact-closed
 * categories.
 *
 * A compact-closed category adds duals to every object — the structure behind
 * RES.RELEASE. The evaluation map ε annihilates a resource–handle pair; the
 * coevaluation map η mints one. The snake equations guarantee round-trip
 * consistency.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  StringDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = THEOREM_COLOR["RES.RELEASE"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-compact-closed",
  kind: "Categorical structure",
  title: "Compact-closed category",
  subtitle: "A* dual · ε : A* ⊗ A → I · η : I → A ⊗ A* · snake equations",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Linear category", href: "#explainer-linear-category" },
  { label: "Dagger compact", href: "#explainer-dagger-compact" },
  { label: "Adjunction", href: "#explainer-adjunction" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>compact-closed category</strong> equips every object{" "}
      <MathInline>{tex`A`}</MathInline> with a dual <MathInline>{tex`A^{*}`}</MathInline>, plus
      evaluation <MathInline>{tex`\varepsilon : A^{*} \otimes A \to I`}</MathInline> and
      coevaluation <MathInline>{tex`\eta : I \to A \otimes A^{*}`}</MathInline>. The snake equations
      guarantee that <MathInline>{tex`A^{**} \cong A`}</MathInline>. CSK uses this to make resource
      release governable: the dual witnesses that a resource can be returned.
    </p>
  ),
  intuition: (
    <>
      <p>
        Think of <MathInline>{tex`A`}</MathInline> as a battery and{" "}
        <MathInline>{tex`A^{*}`}</MathInline> as its charger socket. The evaluation map{" "}
        <MathInline>{tex`\varepsilon`}</MathInline> plugs the battery into the socket and produces
        nothing (the monoidal unit <MathInline>{tex`I`}</MathInline> — "ground state"). The
        coevaluation map <MathInline>{tex`\eta`}</MathInline> conjures a matched battery–socket pair
        from nothing.
      </p>
      <p>
        The snake equations say that creating a pair and immediately plugging one end in gets you
        back where you started — no energy was lost.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        A <strong>compact-closed category</strong> is a symmetric monoidal category{" "}
        <MathInline>{tex`(C, \otimes, I)`}</MathInline> where every object{" "}
        <MathInline>{tex`A`}</MathInline> has a dual object <MathInline>{tex`A^{*}`}</MathInline>{" "}
        together with:
      </p>
      <ul>
        <li>
          <strong>Evaluation</strong>{" "}
          <MathInline>{tex`\varepsilon_{A} : A^{*} \otimes A \to I`}</MathInline>
        </li>
        <li>
          <strong>Coevaluation</strong>{" "}
          <MathInline>{tex`\eta_{A} : I \to A \otimes A^{*}`}</MathInline>
        </li>
      </ul>
      <p>
        These satisfy the <strong>snake (zigzag) equations</strong>:
      </p>
      <MathBlock ariaLabel="Snake equation: (id_A tensor ε_A) composed with (η_A tensor id_A) equals id_A">
        {tex`(\mathrm{id}_{A} \otimes \varepsilon_{A}) \circ (\eta_{A} \otimes \mathrm{id}_{A}) \;=\; \mathrm{id}_{A}`}
      </MathBlock>
      <MathBlock ariaLabel="Snake equation: (ε_A tensor id_A-star) composed with (id_A-star tensor η_A) equals id_A-star">
        {tex`(\varepsilon_{A} \otimes \mathrm{id}_{A^{*}}) \circ (\mathrm{id}_{A^{*}} \otimes \eta_{A}) \;=\; \mathrm{id}_{A^{*}}`}
      </MathBlock>
      <StringDiagram accent={ACCENT} />
    </>
  ),
  relevance: (
    <>
      <p>
        <code>RES.RELEASE</code> uses compact-closed structure: releasing a resource back to the
        system is exactly the evaluation map{" "}
        <MathInline>{tex`\varepsilon : A^{*} \otimes A \to I`}</MathInline> — the holder (
        <MathInline>{tex`A^{*}`}</MathInline>) meets the resource (<MathInline>{tex`A`}</MathInline>
        ) and they annihilate into the ground state (<MathInline>{tex`I`}</MathInline>). The
        coevaluation <MathInline>{tex`\eta`}</MathInline> is how the governance system mints a fresh
        resource–handle pair.
      </p>
      <ul>
        <li>
          <strong>CWE-401</strong> (missing release of memory): the type system enforces that every{" "}
          <MathInline>{tex`\eta`}</MathInline> must be matched by an{" "}
          <MathInline>{tex`\varepsilon`}</MathInline> — the compact-closed structure makes unmatched
          allocations a type error.
        </li>
      </ul>
      <p>
        The{" "}
        <MathInline ariaLabel="(− tensor A) is left adjoint to (A-star tensor −)">
          {tex`(- \otimes A) \dashv (A^{*} \otimes -)`}
        </MathInline>{" "}
        adjunction is the <code>RES.BALANCE</code> Dual Pair Guarantee.
      </p>
    </>
  ),
  references: [
    {
      label: "Theorem Canon (RES.CONSUME, RES.RELEASE, RES.BALANCE)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Intuitionistic Linear Logic in CSK",
      path: "theory/structural/Intuitionistic-Linear-Logic-In-CSK.md",
    },
    {
      label: "RES.RELEASE Neutralization (Agda)",
      path: "agda/Kernel/RES/RELEASE/Neutralization.agda",
    },
  ],
};

export function CompactClosedExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<StringDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
